import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  mergeClasses,
} from "@fluentui/react-components";
import { DismissRegular } from "@fluentui/react-icons";
import { DialogTabs } from "components/dialogTabs/DialogTabs";
import { useEffect, useState } from "react";
import { useSharedDialogContentStyles } from "theme/sharedStyles";
import { useViewEntityDialogStyles } from "./ViewEntityDialog.styles";
import {
  IViewEntityDialogProps,
  IViewEntityField,
} from "./ViewEntityDialog.types";

const FieldList = ({
  fields,
  styles,
}: {
  fields: IViewEntityField[];
  styles: ReturnType<typeof useViewEntityDialogStyles>;
}): JSX.Element => (
  <>
    {fields.map((field) => (
      <div key={field.label} className={styles.fieldContainer}>
        <div className={styles.fieldLabel}>{field.label}</div>
        <div className={styles.fieldValue}>{field.value}</div>
      </div>
    ))}
  </>
);

export const ViewEntityDialog = (
  props: IViewEntityDialogProps,
): JSX.Element => {
  const styles = useViewEntityDialogStyles();
  const sharedContentStyles = useSharedDialogContentStyles();

  const { isOpen, onDismiss, title, fields, tabs, compact } = props;
  const hasTabs = tabs && tabs.length > 0;

  const [selectedTab, setSelectedTab] = useState<string>(
    hasTabs ? tabs[0].key : "",
  );

  useEffect(() => {
    if (isOpen && hasTabs) {
      setSelectedTab(tabs[0].key);
    }
  }, [isOpen, hasTabs, tabs]);

  const handleTabClick = (tabKey: string) => {
    setSelectedTab(tabKey);
  };

  const currentTabIndex = hasTabs
    ? tabs.findIndex((t) => t.key === selectedTab)
    : -1;

  const currentFields = hasTabs
    ? (tabs[currentTabIndex]?.fields ?? [])
    : (fields ?? []);

  return (
    <Dialog
      open={isOpen}
      modalType="modal"
      onOpenChange={(_e, data) => {
        if (!data.open) onDismiss();
      }}
    >
      <DialogSurface
        className={compact ? styles.dialogSurfaceCompact : styles.dialogSurface}
      >
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                icon={<DismissRegular />}
                aria-label="Close"
                onClick={onDismiss}
              />
            }
          >
            {title}
          </DialogTitle>
          <DialogContent
            className={mergeClasses(
              sharedContentStyles.tabbedDialogContent,
              compact ? styles.dialogContentCompact : styles.dialogContent,
            )}
          >
            {hasTabs && (
              <DialogTabs
                tabs={tabs}
                activeTab={selectedTab}
                onTabClick={handleTabClick}
              />
            )}
            <div
              className={
                hasTabs
                  ? mergeClasses(
                      styles.tabContent,
                      sharedContentStyles.scrollableContent,
                    )
                  : undefined
              }
            >
              <FieldList fields={currentFields} styles={styles} />
            </div>
          </DialogContent>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
